import React, { useEffect, useMemo, useRef, useState } from "react";
import { ShoppingBag, Check, X } from "lucide-react";

const TONE = { blue: "#243B52", ochre: "#CF9026", oxblood: "#4E0E18" };

// GTA-style radial merch wheel. Drag with a thumb/finger to spin the selector;
// tap the hub to add to the shared cart. Renders as a full-screen overlay
// (variant="overlay") or embedded inline in the shop panel (variant="inline").
export default function MerchWheel({ items = [], cartCount = 0, onAdd, onClose, onCheckout, onItemClick, requiresSize, variant = "overlay" }) {
  const [active, setActive] = useState(0);
  const [added, setAdded] = useState(null);
  const [dragging, setDragging] = useState(false);
  const wheelRef = useRef(null);
  const audioCtxRef = useRef(null);
  const interacted = useRef(false);
  const prevActive = useRef(0);

  const ensureAudio = () => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioCtxRef.current = new AC();
    }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") audioCtxRef.current.resume();
    interacted.current = true;
  };

  const playTick = () => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = 1300;
    const t = ctx.currentTime;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.09, t + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.045);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.06);
  };

  useEffect(() => {
    if (interacted.current && active !== prevActive.current) playTick();
    prevActive.current = active;
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const n = items.length;
  const step = n ? 360 / n : 0;
  const positions = useMemo(() => items.map((_, i) => -90 + i * step), [items, step]);

  const pickFromPointer = (clientX, clientY) => {
    const el = wheelRef.current;
    if (!el || !n) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const deg = (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI;
    let best = 0, bestDiff = 999;
    positions.forEach((p, i) => {
      const d = Math.abs(((deg - p + 540) % 360) - 180);
      if (d < bestDiff) { bestDiff = d; best = i; }
    });
    setActive(best);
  };

  const onDown = (e) => { ensureAudio(); setDragging(true); const t = e.touches ? e.touches[0] : e; pickFromPointer(t.clientX, t.clientY); };

  // Track drag on the window so it keeps working even outside the wheel bounds
  useEffect(() => {
    if (!dragging) return;
    const move = (e) => { const t = e.touches ? e.touches[0] : e; pickFromPointer(t.clientX, t.clientY); };
    const up = () => setDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [dragging, positions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (variant !== "overlay") return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, variant]);

  const activeItem = items[active];
  const indicatorDeg = positions[active] ?? -90;

  const add = () => {
    if (!activeItem) return;
    if (requiresSize?.(activeItem)) { onItemClick?.(activeItem); return; }
    onAdd?.(activeItem, { qty: 1 });
    setAdded(activeItem.id);
    setTimeout(() => setAdded((a) => (a === activeItem.id ? null : a)), 900);
  };

  const wheel = (
    <div
      ref={wheelRef}
      data-testid="merch-wheel"
      onMouseDown={onDown}
      onTouchStart={onDown}
      className="relative touch-none mx-auto"
      style={{ width: "min(90vw, 560px)", height: "min(90vw, 560px)", cursor: dragging ? "grabbing" : "grab" }}
    >
      <div
        className="absolute inset-0 rounded-full transition-transform duration-200 ease-out"
        style={{
          transform: `rotate(${indicatorDeg + 90}deg)`,
          background: `conic-gradient(from -${step / 2}deg, rgba(207,144,38,0.28) 0deg, rgba(207,144,38,0.28) ${step}deg, transparent ${step}deg)`,
          WebkitMaskImage: "radial-gradient(circle, transparent 33%, #000 33.5%, #000 100%)",
          maskImage: "radial-gradient(circle, transparent 33%, #000 33.5%, #000 100%)",
        }}
      />
      <div className="absolute inset-0 rounded-full border-2 border-[#ECE7DB]/15" />
      <div className="absolute rounded-full border-2 border-[#ECE7DB]/10" style={{ inset: "16%" }} />
      <div
        className="absolute left-1/2 top-1/2 origin-left h-[2px] transition-transform duration-200 ease-out"
        style={{ width: "50%", transform: `translateY(-50%) rotate(${indicatorDeg}deg)`, background: "linear-gradient(90deg, transparent, #CF9026)" }}
      />

      {items.map((it, i) => {
        const ang = (positions[i] * Math.PI) / 180;
        const R = 41;
        const x = 50 + R * Math.cos(ang);
        const y = 50 + R * Math.sin(ang);
        const isActive = i === active;
        return (
          <button
            key={it.id}
            data-testid={`wheel-item-${it.id}`}
            onClick={(e) => { e.stopPropagation(); ensureAudio(); setActive(i); onItemClick?.(it); }}
            className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full overflow-hidden border-2 transition-all duration-200"
            style={{
              left: `${x}%`, top: `${y}%`,
              width: isActive ? 86 : 58, height: isActive ? 86 : 58,
              borderColor: isActive ? "#CF9026" : "rgba(236,231,219,0.25)",
              boxShadow: isActive ? "0 0 0 4px rgba(207,144,38,0.25), 0 10px 30px rgba(0,0,0,0.5)" : "none",
              background: TONE[it.tone] || "#4E0E18",
              zIndex: isActive ? 10 : 5,
            }}
          >
            <img src={it.img} alt={it.name} className="w-full h-full object-cover" />
          </button>
        );
      })}

      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full flex flex-col items-center justify-center text-center px-6"
        style={{ width: "33%", height: "33%", background: "#15100E", border: "2px solid rgba(236,231,219,0.2)" }}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {activeItem ? (
          <>
            <p className="font-display font-extrabold stretch-text text-[#ECE7DB] leading-[0.9]" style={{ fontSize: "clamp(13px,2.2vw,20px)" }}>{activeItem.name}</p>
            <p className="font-tech text-[#CF9026] text-sm mt-1">{typeof activeItem.price === "number" ? `$${activeItem.price}` : activeItem.price}</p>
            <button
              data-testid="wheel-add"
              onClick={(e) => { e.stopPropagation(); add(); }}
              className="mt-3 font-tech text-[10px] tracking-[0.2em] px-4 py-2 flex items-center gap-2 transition-colors"
              style={{ background: added === activeItem.id ? "#CF9026" : "#ECE7DB", color: "#15100E" }}
            >
              {added === activeItem.id ? (<><Check size={13} /> ADDED</>) : (<><ShoppingBag size={13} /> ADD TO CART</>)}
            </button>
          </>
        ) : (
          <p className="font-tech text-[#ECE7DB]/50 text-xs tracking-widest">LOADING…</p>
        )}
      </div>
    </div>
  );

  // Embedded in the shop panel
  if (variant === "inline") {
    return (
      <div data-testid="merch-wheel-inline" className="w-full rounded-lg border-2 border-[#15100E] py-8 px-4" style={{ background: "radial-gradient(circle at 50% 45%, #241a16, #15100E 72%)" }}>
        <p className="text-center font-tech text-[#ECE7DB]/50 text-[11px] tracking-[0.25em] mb-6">DRAG TO SPIN · TAP AN ITEM TO ENLARGE · TAP CENTER TO ADD</p>
        {wheel}
      </div>
    );
  }

  // Full-screen overlay
  return (
    <div
      data-testid="merch-wheel-overlay"
      className="fixed inset-0 z-[98] grain overflow-hidden select-none"
      style={{ background: "radial-gradient(circle at 50% 45%, #241a16, #15100E 70%)" }}
    >
      <div className="absolute top-0 inset-x-0 h-16 flex items-center justify-between px-6 text-[#ECE7DB] z-20">
        <span className="font-tech text-[10px] tracking-[0.3em] text-[#CF9026]">ILLER · MERCH WHEEL</span>
        <div className="flex items-center gap-3">
          <button data-testid="wheel-view-cart" onClick={onCheckout} className="font-tech text-xs tracking-[0.2em] flex items-center gap-2 hover:text-[#CF9026] transition-colors">
            <ShoppingBag size={15} /> VIEW CART ({cartCount})
          </button>
          <button data-testid="wheel-close" onClick={onClose} className="h-9 w-9 bg-[#ECE7DB] text-[#15100E] flex items-center justify-center hover:bg-[#CF9026] transition-colors">
            <X size={18} />
          </button>
        </div>
      </div>
      <p className="absolute top-14 inset-x-0 text-center font-tech text-[#ECE7DB]/50 text-[11px] tracking-[0.25em] z-20">
        DRAG WITH YOUR THUMB · TAP CENTER TO ADD
      </p>
      <div className="absolute inset-0 flex items-center justify-center">{wheel}</div>
    </div>
  );
}
